import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { UserSearch, Loader2, Ban, Wallet, ExternalLink, CalendarClock } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge, RiskBadge } from "@/components/common/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { listResource, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MovimientosTimeline } from "@/components/cliente/MovimientosTimeline";
import { FactorajeMovimientos } from "@/components/cliente/FactorajeMovimientos";
import { ArrendamientoMovimientos } from "@/components/cliente/ArrendamientoMovimientos";
import { ReasignarCliente } from "@/components/cliente/ReasignarCliente";
import { CreditoCard, PagosSinCredito } from "@/components/cliente/CreditoCard";
import { CreditosContratos } from "@/components/cliente/CreditosContratos";
import { mxn, fdate } from "@/lib/format";
import { ExpedienteDigital } from "@/components/common/ExpedienteDigital";
import { UbicacionGuardada } from "@/components/common/UbicacionGuardada";
import { SicConsulta } from "@/components/common/SicConsulta";
import { GradoRiesgo } from "@/components/riesgo/GradoRiesgo";
import { DesbloqueoConCarta } from "@/components/screening/DesbloqueoConCarta";
import { VerFichaButton } from "@/components/screening/FichaCoincidencia";
import { ConstanciaButton } from "@/components/screening/FichaPartes";
import { SenalesRiesgoTimeline } from "@/components/riesgo/SenalesRiesgoTimeline";
import { PerfilTransaccional } from "@/components/pld/PerfilTransaccional";
import { PerfilTransaccionalInmo, SemaforoTx } from "@/components/inmobiliaria/PerfilTransaccionalInmo";
import { PuiSenalBanner } from "@/components/pld/PuiSenalBanner";
import { MigracionAvisoCliente } from "@/components/migracion/MigracionAvisoCliente";
import { AvisoCreditoMigrado } from "@/components/migracion/AvisoCreditoMigrado";
import { AvisosFiscalesCreditos } from "@/components/fiscal/AvisosDatosFiscales";
import { AutorizacionesRiesgoAlto } from "@/components/pld/AutorizacionesRiesgoAlto";
import { ReciboPagoButton } from "@/components/common/ReciboPagoButton";
import { DeudorFactorajeBadge, NombreConDistintivo } from "@/components/common/DeudorFactorajeBadge";
import { ProveedorArrendamientoBadge } from "@/components/common/ProveedorArrendamientoBadge";

const COLS = [
  { key: "tipo_persona", label: "Tipo", kind: "badge" },
  { key: "nombre", label: "Cliente", render: (r) => <NombreConDistintivo row={r} /> },
  { key: "rfc", label: "RFC" },
  { key: "nivel_riesgo", label: "Riesgo", kind: "risk" },
  { key: "expediente_estatus", label: "Expediente", kind: "badge" },
];

// feb-2026 · iter 07-31 — Columna EXCLUSIVA del giro inmobiliaria: muestra el peor semáforo
// del cliente contra el umbral de acumulación 6 meses. La lista SOFOM queda idéntica.
const COL_UMBRAL_INMO = {
  key: "_semaforo_tx", label: "Umbral 6m",
  render: (r) => <SemaforoTx estado={r._semaforo_tx} porcentaje={r._semaforo_tx_porc} />,
};

function MiniTable({ rows, columns, empty, actions }) {
  return <DataTable rows={rows || []} columns={columns} emptyLabel={empty} actions={actions} />;
}

const ESTATUS_MATCH = { pendiente: "Pendiente", confirmada: "Confirmada", descartada_falso_positivo: "Falso positivo",
  descartada: "Descartada (umbral)", anulada_por_reversion: "Versión revertida" };

export default function Cliente360() {
  const [clientes, setClientes] = useState([]);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("general");
  const [movKey, setMovKey] = useState(0);
  // feb-2026 · iter 07-26 — Operaciones Fr. V del cliente actual (solo giro=inmobiliaria).
  // Se fetchea aparte del perfil360 (que es compartido con SOFOM). El endpoint
  // /inmobiliaria/operaciones no acepta filtro por cliente_id, así que se filtra en front.
  const [opsCliente, setOpsCliente] = useState([]);
  // feb-2026 · iter 07-31 — Bulk de semáforos de umbral 6m por cliente (solo giro inmo).
  // Se fetchea una vez al montar la lista; enriquece los rows con _semaforo_tx.
  const [semaforosTx, setSemaforosTx] = useState({});
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const esInmobiliaria = (user?.giro || "").toLowerCase() === "inmobiliaria";

  useEffect(() => { listResource("clientes").then(setClientes); }, []);

  // feb-2026 · iter 07-31 — Fetch bulk semáforos umbral 6m (solo giro inmobiliaria).
  useEffect(() => {
    if (!esInmobiliaria) return;
    api.get("/inmobiliaria/clientes/semaforos-transaccionales")
      .then((r) => setSemaforosTx(r.data?.por_cliente || {}))
      .catch(() => setSemaforosTx({}));
  }, [esInmobiliaria]);

  // Enriquecer clientes con datos del semáforo (sin mutar el array original).
  const clientesConSemaforo = esInmobiliaria
    ? clientes.map((c) => ({
        ...c,
        _semaforo_tx: semaforosTx[c.id]?.peor_semaforo,
        _semaforo_tx_porc: semaforosTx[c.id]?.peor_porcentaje,
      }))
    : clientes;
  const colsEfectivas = esInmobiliaria ? [...COLS, COL_UMBRAL_INMO] : COLS;

  const ver = async (row) => {
    setOpen(true);
    setTab("general");
    setLoading(true);
    setData(null);
    setOpsCliente([]);
    try {
      const res = await api.get(`/clientes/${row.id}/perfil360`).then((r) => r.data);
      setData(res);
      if (esInmobiliaria) {
        try {
          const ops = await api.get("/inmobiliaria/operaciones").then((r) => r.data);
          setOpsCliente((ops || []).filter((o) =>
            (o.partes || []).some((p) => p.cliente_id === row.id)));
        } catch { setOpsCliente([]); }
      }
    } finally { setLoading(false); }
  };

  // feb-2026 · iter 07-26 — Auto-abrir la ficha cuando la URL trae ?id=<cliente_id>.
  // Se dispara UNA sola vez cuando la lista de clientes ya cargó (para poder resolver el row).
  useEffect(() => {
    const idParam = searchParams.get("id");
    const tabParam = searchParams.get("tab"); if (tabParam) setTab(tabParam);
    if (!idParam || clientes.length === 0 || open) return;
    const row = clientes.find((x) => x.id === idParam);
    if (row) ver(row);
  }, [clientes, searchParams]);

  // Refresco suave: actualiza datos sin desmontar la pestaña activa (conserva el resultado de la consulta)
  const refresh = async () => {
    if (!data?.cliente) return;
    const res = await api.get(`/clientes/${data.cliente.id}/perfil360`).then((r) => r.data);
    setData(res);
  };

  const c = data?.cliente;
  const sicNoAutoriza = (data?.autorizaciones_sic || []).find((a) => a.decision === "no_autoriza");
  const sicTieneVigente = (data?.autorizaciones_sic || []).some((a) => a.estatus === "Vigente");

  return (
    <div>
      <PageHeader title="Perfil 360 del cliente" subtitle="Vista integral: expediente, crédito, PLD, CONDUSEF, Círculo/Buró, alertas y bitácora" />
      <div className="p-6 md:p-8">
        <DataTable testid="table-clientes-360" columns={colsEfectivas} rows={clientesConSemaforo} onRowClick={ver}
          emptyLabel="Sin clientes" />
        <p className="mt-3 text-xs text-slate-400">Haz clic en un cliente para abrir su perfil 360.</p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-8">
              <DialogTitle className="flex items-center gap-2 font-heading">
                <UserSearch className="h-5 w-5 text-primary" /> {c?.nombre || "Perfil 360"}
                {c?.es_deudor_factoraje === true && <DeudorFactorajeBadge testid="c360-badge-deudor-factoraje" />}
                {c?.es_proveedor_arrendamiento === true && <ProveedorArrendamientoBadge testid="c360-badge-proveedor-arrendamiento" />}
              </DialogTitle>
              {!loading && data && user?.role === "admin_entidad" && (
                <ReasignarCliente cliente={c} onDone={() => { refresh(); setMovKey((k) => k + 1); }} />
              )}
            </div>
            {/* Trazabilidad del grado: metodología con la que se selló + fecha + enlace al
                prospecto padre si existe. Antes el operador no sabía si el grado que veía
                venía de la v1 o de la v2 vigente. */}
            {!loading && data && c && (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500" data-testid="c360-riesgo-meta">
                <span className="inline-flex items-center gap-1">
                  Riesgo:
                  <RiskBadge value={c.nivel_riesgo} />
                  {c.metodologia_version && <span>· metodología v{c.metodologia_version}</span>}
                  {c.updated_at && <span>· al {String(c.updated_at).slice(0, 10)}</span>}
                </span>
                {c.origen_prospecto_id && data?.prospecto_padre && (
                  <span className="inline-flex items-center gap-1" data-testid="c360-prospecto-padre">
                    Proviene del prospecto:
                    <a href={`/prospectos?id=${c.origen_prospecto_id}`}
                       className="font-medium text-primary underline-offset-2 hover:underline">
                      {data.prospecto_padre.nombre || data.prospecto_padre.razon_social || c.origen_prospecto_id.slice(0, 8)}
                    </a>
                  </span>
                )}
              </div>
            )}
          </DialogHeader>
          {loading || !data ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-300" /></div>
          ) : (
            <>
            {/* feb-2026 · iter 07-26 — <PuiSenalBanner> es señal PUI (régimen SOFOM/CNBV);
                no aplica al vertical Fr. V y se oculta cuando el user es de giro inmobiliaria. */}
            {!esInmobiliaria && <PuiSenalBanner clienteId={c?.id} />}
            <MigracionAvisoCliente cliente={c} />
            {sicNoAutoriza && !sicTieneVigente && !esInmobiliaria && (
              <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="c360-no-autoriza-banner">
                <Ban className="mt-0.5 h-4 w-4 shrink-0" />
                <span><b>No autorizó consulta a Buró/Círculo.</b> Sellado por {sicNoAutoriza.usuario} el {sicNoAutoriza.fecha} {sicNoAutoriza.hora}. El crédito se gestiona sin consulta SIC.</span>
              </div>
            )}
            <Tabs value={tab} onValueChange={setTab} className="min-w-0">
              {/* sep-2026 · `h-auto`: con 12 pestañas la lista envuelve a 3 renglones y con la
                  altura fija (h-9 del componente base) los renglones 2 y 3 se salían de la caja
                  y quedaban TAPADOS por el panel — en móvil «Créditos/Pagos» era inalcanzable. */}
              <TabsList className="mb-4 h-auto flex-wrap gap-1 py-1">
                <TabsTrigger value="general" data-testid="c360-general">Datos generales</TabsTrigger>
                <TabsTrigger value="ubicacion" data-testid="c360-ubicacion">Ubicación</TabsTrigger>
                <TabsTrigger value="grado" data-testid="c360-grado">Grado de riesgo</TabsTrigger>
                <TabsTrigger value="expediente">Expediente</TabsTrigger>
                {/* Tabs SOFOM (crédito). En inmobiliaria Fr. V el cliente no tiene créditos,
                    cobranza, reclamaciones CONDUSEF ni consulta a Buró/Círculo. */}
                {!esInmobiliaria && <TabsTrigger value="creditos">Créditos/Pagos</TabsTrigger>}
                {/* Fase 5 (feb-2026) — Tab condicional visible SOLO si el cliente tiene al menos
                    una línea de crédito revolvente. Cliente sin línea → tab NO aparece → Perfil
                    360 idéntico al comportamiento anterior a Fase 5. */}
                {!esInmobiliaria && (data.lineas || []).length > 0 && (
                  <TabsTrigger value="lineas" data-testid="c360-tab-lineas">Línea de crédito</TabsTrigger>
                )}
                {!esInmobiliaria && <TabsTrigger value="cobranza">Cobranza</TabsTrigger>}
                <TabsTrigger value="pld">PLD</TabsTrigger>
                {!esInmobiliaria && <TabsTrigger value="condusef">CONDUSEF</TabsTrigger>}
                {!esInmobiliaria && <TabsTrigger value="sic" data-testid="c360-sic">Círculo/Buró</TabsTrigger>}
                <TabsTrigger value="screening" data-testid="c360-screening">Listas/Screening</TabsTrigger>
                {/* Tab propia del vertical inmobiliaria: operaciones Fr. V del cliente. */}
                {esInmobiliaria && <TabsTrigger value="operaciones" data-testid="c360-operaciones">Operaciones</TabsTrigger>}
                <TabsTrigger value="bitacora">Bitácora</TabsTrigger>
                <TabsTrigger value="movimientos" data-testid="c360-tab-movimientos">Movimientos</TabsTrigger>
              </TabsList>

              <TabsContent value="general">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {[["Tipo", c.tipo_persona], ["RFC", c.rfc], ["CURP", c.curp], ["Riesgo", c.nivel_riesgo], ["PEP", c.pep ? "Sí" : "No"], ["Listas", c.lista_bloqueados], ["Teléfono", c.telefono], ["Correo", c.correo], ["Actividad", c.actividad_economica]].map(([l, v]) => (
                    <div key={l} className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
                      <p className="mt-0.5 text-sm font-medium text-slate-800">{v || "—"}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="ubicacion">
                {(data.ubicaciones && data.ubicaciones.length) ? (
                  <div className="grid gap-3 sm:grid-cols-2" data-testid="c360-ubicaciones">
                    {data.ubicaciones.map((u, i) => (
                      <UbicacionGuardada key={i} geo={u} label={u.tipo || "Ubicación"} testid={`c360-ubic-${i}`} />
                    ))}
                  </div>
                ) : (
                  <p className="py-8 text-center text-sm text-slate-400" data-testid="c360-sin-ubicacion">Sin ubicación registrada</p>
                )}
              </TabsContent>

              <TabsContent value="grado">
                <GradoRiesgo clienteId={c.id} cliente={c} onChange={refresh} />
              </TabsContent>

              <TabsContent value="expediente">
                <ExpedienteDigital clienteId={c.id} cliente={c} onChange={refresh} />
              </TabsContent>

              <TabsContent value="creditos">
                {(data.creditos || []).length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-400" data-testid="c360-sin-creditos">
                    Sin créditos registrados para este cliente.
                  </p>
                ) : (
                  <div className="space-y-3" data-testid="c360-creditos-lista">
                    {/* sep-2026 · Cada crédito con su folio, producto, monto, estatus y SU
                        contrato firmado. Dos créditos → dos contratos, sin cruzarse. */}
                    <AvisoCreditoMigrado creditos={data.creditos} />
                    <AvisosFiscalesCreditos creditos={data.creditos} />
                    <CreditosContratos
                      creditos={data.creditos}
                      documentos={data.documentos}
                    />
                    {data.creditos.map((cr) => {
                      // Garantía asociada al crédito (si aplica). Cada crédito tiene la suya propia.
                      // Fase 6 (feb-2026) — Fallback aditivo: si el crédito es DISPOSICIÓN de una línea
                      // (linea_id presente), busca también garantías registradas contra la línea. Un
                      // crédito simple no tiene `linea_id`, así que el fallback nunca se activa; la
                      // primera búsqueda por credito_id sigue teniendo prioridad. Regresión cero.
                      const g = (data.garantias || []).find((x) => x.credito_id === cr.id)
                              || (cr.linea_id ? (data.garantias || []).find((x) => x.linea_id === cr.linea_id) : null);
                      return (
                        <CreditoCard
                          key={cr.id}
                          credito={cr}
                          pagos={data.pagos}
                          garantia={g}
                          perfilClienteId={c?.id}
                          testid={`c360-credito-${(cr.numero_credito || cr.id).replace(/[^A-Za-z0-9-]/g, "")}`}
                        />
                      );
                    })}
                    {/* Defensa audit: pagos sin credito_id (legacy) — se muestran para no perderlos. */}
                    <PagosSinCredito pagos={data.pagos} />
                  </div>
                )}
              </TabsContent>

              {/* Fase 5 (feb-2026) — Tab de Línea de crédito revolvente. Solo se renderiza si
                  el cliente tiene al menos una línea (el trigger es condicional). */}
              <TabsContent value="lineas">
                <LineasClienteTab lineas={data.lineas || []} creditos={data.creditos || []} garantias={data.garantias || []} />
              </TabsContent>

              <TabsContent value="cobranza">
                <MiniTable rows={data.cobranza} empty="Sin gestiones de cobranza"
                  columns={[{ key: "numero_credito", label: "Crédito" }, { key: "estado_cobranza", label: "Estado", kind: "badge" }, { key: "dias_atraso", label: "Atraso" }, { key: "estatus_gestion", label: "Gestión", kind: "badge" }]} />
              </TabsContent>

              <TabsContent value="pld">
                <div className="space-y-5">
                  <PerfilTransaccional clienteId={c.id} onChange={refresh} />
                  <AutorizacionesRiesgoAlto autorizaciones={data.autorizaciones_riesgo_alto} />
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Alertas PLD del cliente</p>
                    <MiniTable rows={data.alertas} empty="Sin alertas"
                      columns={[{ key: "folio", label: "Folio" }, { key: "regla", label: "Regla" }, { key: "nivel_riesgo", label: "Riesgo", kind: "risk" }, { key: "estatus", label: "Estatus", kind: "badge" }]} />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="condusef">
                <MiniTable rows={data.condusef_reune} empty="Sin registros REUNE"
                  columns={[{ key: "folio_interno", label: "Folio" }, { key: "tipo", label: "Tipo", kind: "badge" }, { key: "causa", label: "Causa" }, { key: "estatus", label: "Estatus", kind: "badge" }]} />
                <div className="mt-3"><MiniTable rows={data.condusef_redeco} empty="Sin quejas REDECO"
                  columns={[{ key: "folio", label: "Folio" }, { key: "despacho", label: "Despacho" }, { key: "motivo", label: "Motivo" }, { key: "estatus", label: "Estatus", kind: "badge" }]} /></div>
              </TabsContent>

              <TabsContent value="sic">
                <SicConsulta cliente={c} autorizaciones={data.autorizaciones_sic}
                  consultas={data.consultas_sic} onChange={refresh} />
              </TabsContent>

              <TabsContent value="screening">
                {/* Historial cronológico de señales de riesgo — distingue autoridad vs. interna */}
                <div className="mb-4">
                  <SenalesRiesgoTimeline cliente={data.cliente} perfil360={data} />
                </div>
                {data.bloqueado_lpb && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="c360-bloqueo-banner">
                    <span className="font-semibold" data-testid="c360-bloqueo-lista">
                      Cliente BLOQUEADO — coincidencia CONFIRMADA en {data.bloqueo_lista_label || data.bloqueo_lista || "lista de sanción"}
                      {data.bloqueo_score ? ` (score ${data.bloqueo_score})` : ""}.
                    </span>
                    <span>{data.bloqueo_motivo} — impide comité, dispersión y pago a tercero.</span>
                  </div>
                )}
                {/* Panel de desbloqueo con carta PDF — sólo OC, sólo si está bloqueado.
                    Diseño distintivo: rojo intenso + copy "distinto del override 43ª". */}
                {data.bloqueado_lpb && (
                  <DesbloqueoConCarta
                    clienteId={c.id}
                    bloqueoLista={data.bloqueo_lista_label || data.bloqueo_lista}
                    bloqueoScore={data.bloqueo_score}
                    bloqueoMotivo={data.bloqueo_motivo}
                    onDone={refresh}
                  />
                )}
                {/* Trazabilidad del último desbloqueo cuando existe historial. */}
                {!data.bloqueado_lpb && data.desbloqueo_por && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
                       data-testid="c360-desbloqueo-historico">
                    <span className="font-semibold uppercase tracking-wide">Desbloqueo por sanción registrado</span>
                    <span>
                      El {data.desbloqueo_fecha} · por <b>{data.desbloqueo_por}</b>
                      {data.desbloqueo_motivo ? <> · motivo: {data.desbloqueo_motivo}</> : null}
                      {data.desbloqueo_carta_doc_id ? <> · carta PDF adjunta ({String(data.desbloqueo_carta_doc_id).slice(0, 8)}…)</> : null}.
                    </span>
                  </div>
                )}
                {/* feb-2026 (P0-B) — Cotejo externo pendiente: la tabla vacía NO puede
                    interpretarse como "limpio". Banner ámbar antes de la tabla. */}
                {data.screening_pendiente && !data.bloqueado_lpb && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="c360-sin-verificar-banner">
                    <span className="font-semibold uppercase tracking-wide text-xs">Sin verificar — cotejo externo pendiente.</span>
                    <span className="text-xs">
                      El proveedor de listas (cotejo externo) no completó la consulta al alta.
                      {data.screening_pendiente_motivo ? (
                        <> Motivo: <span className="font-mono">{String(data.screening_pendiente_motivo).split(":")[0]}</span>.</>
                      ) : null}
                      {" "}Una tabla vacía en este estado <b>no</b> significa "sin coincidencias" — significa que aún no se verificó.
                    </span>
                  </div>
                )}
                {c?.migracion_coincidencia_pendiente && (data.screening_matches || []).some((m) => m.estatus === "pendiente") && (
                  <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="c360-migracion-coincidencia-banner">
                    Coincidencia en listas locales detectada en migración de cartera. Pendiente de dictamen del Oficial de Cumplimiento.
                  </div>
                )}
                <MiniTable rows={data.screening_matches}
                  empty={data.screening_pendiente ? "Cotejo pendiente — la ausencia de filas no implica limpieza" : "Sin coincidencias en listas"}
                  columns={[
                    { key: "lista_nombre", label: "Lista", kind: "badge" },
                    { key: "entrada_nombre", label: "Registro" },
                    { key: "score", label: "Score" },
                    { key: "tipo_coincidencia", label: "Tipo", kind: "badge" },
                    { key: "fuente_externa", label: "Fuente", render: (r) => r.fuente_externa === "q-detect" ? "Cotejo externo" : "Listas locales" },
                    { key: "estatus", label: "Decisión", render: (r) => <StatusBadge value={ESTATUS_MATCH[r.estatus] || r.estatus} /> },
                  ]}
                  actions={(r) => (
                    <div className="flex justify-end gap-1.5" data-testid={`c360-match-acciones-${r.id}`}>
                      <VerFichaButton match={r} onDecided={refresh} />
                      {r.estatus !== "pendiente" && r.estatus !== "anulada_por_reversion" && (
                        <ConstanciaButton matchId={r.id} folio={r.constancia_folio} compact testid={`c360-constancia-${r.id}`} />
                      )}
                    </div>
                  )} />
                <div className="mt-4" data-testid="c360-qeq-evidencias">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Consultas de cotejo externo (evidencia regulatoria cifrada)</p>
                  <MiniTable rows={data.qeq_evidencias} empty="Sin consultas de cotejo externo registradas"
                    columns={[
                      { key: "fecha", label: "Fecha de consulta", render: (r) => (r.fecha || "").slice(0, 19).replace("T", " ") || "—" },
                      { key: "folio", label: "Folio", render: (r) => <span className="font-mono text-xs">{r.folio || "—"}</span> },
                      { key: "id_personas", label: "ID_PERSONA (Campo Auditor)", render: (r) => (r.id_personas || []).length ? <span className="font-mono text-xs">{r.id_personas.join(", ")}</span> : "—" },
                      { key: "sin_datos", label: "Resultado", render: (r) => r.sin_datos ? "Sin registros en listas" : "Con registros" },
                    ]} />
                  <p className="mt-2 text-[11px] text-slate-400">La respuesta cruda del proveedor se conserva cifrada en el expediente para peritaje CNBV.</p>
                </div>
              </TabsContent>

              <TabsContent value="bitacora">
                <MiniTable rows={data.bitacora} empty="Sin eventos en bitácora"
                  columns={[{ key: "fecha", label: "Fecha", kind: "date" }, { key: "accion", label: "Acción" }, { key: "modulo", label: "Módulo", kind: "badge" }, { key: "usuario", label: "Usuario" }]} />
              </TabsContent>

              {/* feb-2026 · iter 07-26 — Tab EXCLUSIVA del vertical inmobiliaria: operaciones
                  Fr. V del cliente (compraventa/desarrollo/arrendamiento). Reusa el shape
                  del backend db.operaciones_inmobiliarias filtrado por partes.cliente_id.
                  feb-2026 · iter 07-31 — Encabezado con el WIDGET de perfil transaccional
                  (acumulado 6m por fracción, semáforo, reinicios, umbral) + tabla enriquecida
                  con %-al-momento y detonación de aviso. Todo condicionado a giro=inmobiliaria. */}
              {esInmobiliaria && (
                <TabsContent value="operaciones" className="space-y-4">
                  <PerfilTransaccionalInmo clienteId={c?.id} />
                  <MiniTable rows={opsCliente}
                    empty="Sin operaciones registradas para este cliente"
                    columns={[
                      { key: "fecha", label: "Fecha", kind: "date" },
                      { key: "fraccion", label: "Fracción", kind: "badge" },
                      { key: "tipo", label: "Tipo", kind: "badge" },
                      { key: "monto_sin_iva", label: "Monto (sin IVA)", render: (r) => mxn(r.monto_sin_iva) },
                      { key: "porc_al_momento", label: "% al momento",
                        render: (r) => {
                          const p = (r.acumulacion_6m || {}).porcentaje_alcanzado;
                          if (p == null) return <span className="text-slate-400">—</span>;
                          const cls = p >= 100
                            ? "border-rose-300 bg-rose-50 text-rose-700"
                            : p >= 80
                              ? "border-amber-300 bg-amber-50 text-amber-700"
                              : "border-slate-300 bg-slate-50 text-slate-700";
                          return <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium ${cls}`}>{p}%</span>;
                        } },
                      { key: "detono_aviso", label: "Aviso SPPLD",
                        render: (r) => {
                          const detono = (r.acumulacion_6m || {}).objeto_de_aviso_por_acumulacion;
                          const indiv = r.objeto_de_aviso_individual;
                          if (detono) return <span className="inline-flex items-center rounded border border-rose-300 bg-rose-50 px-1.5 py-0.5 text-[11px] font-medium text-rose-700" data-testid="op-detono-acum">Detonó (acum.)</span>;
                          if (indiv) return <span className="inline-flex items-center rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700" data-testid="op-detono-indiv">Aviso individual</span>;
                          if (r.objeto_de_aviso) return <span className="inline-flex items-center rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">Sí</span>;
                          return <span className="text-slate-400">No</span>;
                        } },
                      { key: "estatus", label: "Estatus", kind: "badge" },
                    ]} />
                </TabsContent>
              )}

              <TabsContent value="movimientos">
                {(data.creditos || []).filter((cr) => cr.movimientos_factoraje).map((cr) => (
                  <FactorajeMovimientos key={cr.id} credito={cr}
                    testid={`c360-mov-fx-${(cr.numero_credito || cr.id).replace(/[^A-Za-z0-9-]/g, "")}`} />
                ))}
                {(data.creditos || []).filter((cr) => cr.movimientos_arrendamiento?.length).map((cr) => (
                  <ArrendamientoMovimientos key={cr.id} credito={cr}
                    testid={`c360-mov-arr-${(cr.numero_credito || cr.id).replace(/[^A-Za-z0-9-]/g, "")}`} />
                ))}
                {c && tab === "movimientos" && <MovimientosTimeline clienteId={c.id} reloadKey={movKey} />}
              </TabsContent>
            </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Fase 5 (feb-2026) — Pestaña de Línea de crédito revolvente (Perfil 360).
//
// Renderiza cada línea del cliente con: números clave (límite/dispuesto/disponible),
// vigencia + FECHA DE VENCIMIENTO con semáforo (rojo ≤30 días, amarillo ≤90 días,
// verde >90 días; línea Vencida = rojo permanente), estatus y lista de sus
// disposiciones enlazadas al detalle. Cero persistencia; response-only.
// ─────────────────────────────────────────────────────────────────────────────
function _semaforoVencimiento(fecha_vencimiento, estatus) {
  // Devuelve la config visual del semáforo: color + días restantes + label.
  // Criterio unificado con otros semáforos del sistema (rojo/amarillo/verde).
  if (!fecha_vencimiento) return null;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fv = new Date(fecha_vencimiento);
  fv.setHours(0, 0, 0, 0);
  const dias = Math.floor((fv - hoy) / (1000 * 60 * 60 * 24));
  const estatusVencido = String(estatus || "").toLowerCase() === "vencida";
  if (estatusVencido || dias < 0) {
    return { color: "rose", label: `Vencida hace ${Math.abs(dias)} día${Math.abs(dias) === 1 ? "" : "s"}`, dias };
  }
  if (dias <= 30) {
    return { color: "rose", label: `Vence en ${dias} día${dias === 1 ? "" : "s"}`, dias };
  }
  if (dias <= 90) {
    return { color: "amber", label: `Vence en ${dias} días`, dias };
  }
  return { color: "emerald", label: `${dias} días restantes`, dias };
}

const _SEMAF_COLORS = {
  rose: "border-rose-300 bg-rose-50 text-rose-700",
  amber: "border-amber-300 bg-amber-50 text-amber-800",
  emerald: "border-emerald-300 bg-emerald-50 text-emerald-700",
};

function LineasClienteTab({ lineas, creditos, garantias }) {
  if (!lineas || lineas.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-400" data-testid="c360-sin-lineas">
      Sin líneas de crédito registradas para este cliente.
    </p>;
  }
  return (
    <div className="space-y-4" data-testid="c360-lineas-lista">
      {lineas.map((lin) => {
        const disposiciones = (creditos || []).filter((c) => c.linea_id === lin.id);
        // Fase 6 (feb-2026) — garantías registradas contra esta línea (linea_id match).
        const gLineaLista = (garantias || []).filter((g) => g.linea_id === lin.id);
        const sem = _semaforoVencimiento(lin.fecha_vencimiento, lin.estatus);
        const testidBase = `c360-linea-${(lin.numero_linea || lin.id).replace(/[^A-Za-z0-9-]/g, "")}`;
        return (
          <div key={lin.id} className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={testidBase}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-100 px-4 py-3">
              <Wallet className="h-4 w-4 shrink-0 text-primary" />
              <Link
                to={`/lineas/${lin.id}`}
                className="font-mono text-sm font-semibold text-primary hover:underline"
                data-testid={`${testidBase}-link`}
              >
                {lin.numero_linea}
              </Link>
              <StatusBadge value={lin.estatus} />
              {sem && (
                <span
                  data-testid={`${testidBase}-semaforo`}
                  data-color={sem.color}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${_SEMAF_COLORS[sem.color]}`}
                  title={`Fecha de vencimiento: ${fdate(lin.fecha_vencimiento)}`}
                >
                  <CalendarClock className="h-3 w-3" />
                  {sem.label}
                </span>
              )}
              <div className="ml-auto text-xs text-slate-400">
                Vence {fdate(lin.fecha_vencimiento)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
              {[
                ["Límite autorizado", mxn(lin.limite_autorizado), "text-slate-800"],
                ["Dispuesto", mxn(lin.dispuesto || 0), "text-amber-700"],
                ["Disponible", mxn(lin.disponible ?? lin.limite_autorizado), "text-emerald-700"],
                ["Vigencia", lin.vigencia_meses ? `${lin.vigencia_meses} meses` : "—", "text-slate-800"],
              ].map(([l, v, cls]) => (
                <div key={l} className="rounded-md border border-slate-200 bg-slate-50 p-2.5" data-testid={`${testidBase}-${l}`}>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
                  <p className={`mt-0.5 text-sm font-semibold tabular-nums ${cls}`}>{v}</p>
                </div>
              ))}
            </div>
            {/* Fase 6 (feb-2026) — Garantías ligadas a la línea. Solo se renderiza si
                al menos una garantía tiene `linea_id === lin.id`. Si no hay, no aparece
                (cero ruido en pantalla). */}
            {gLineaLista.length > 0 && (
              <div className="border-t border-slate-100 px-4 py-3" data-testid={`${testidBase}-garantias`}>
                <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Garantía ({gLineaLista.length})
                </h5>
                <div className="space-y-1.5 text-xs">
                  {gLineaLista.map((g) => {
                    const cobPct = (g.valor_garantia && lin.limite_autorizado)
                      ? (Number(g.valor_garantia) / Number(lin.limite_autorizado)) * 100 : null;
                    const insuf = cobPct !== null && cobPct < 100;
                    return (
                      <div key={g.id} className={`flex flex-wrap items-center gap-2 rounded-md border px-2 py-1.5 ${insuf ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
                        <span className="font-semibold text-slate-800">{g.tipo_garantia}</span>
                        {g.valor_garantia > 0 && (
                          <span className="tabular-nums text-slate-700">{mxn(g.valor_garantia)}</span>
                        )}
                        {g.descripcion && <span className="text-slate-500">· {g.descripcion}</span>}
                        {cobPct !== null && (
                          <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${insuf ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`}>
                            Cobertura {cobPct.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="border-t border-slate-100 px-4 py-3">
              <div className="mb-2 flex items-center justify-between">
                <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Disposiciones ({disposiciones.length})
                </h5>
                <Link
                  to={`/lineas/${lin.id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  data-testid={`${testidBase}-abrir`}
                >
                  Abrir detalle <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
              {disposiciones.length === 0 ? (
                <p className="text-xs text-slate-400">La línea aún no tiene disposiciones ejercidas.</p>
              ) : (
                <div className="overflow-hidden rounded-md border border-slate-200">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-2 py-1.5">Disposición</th>
                        <th className="px-2 py-1.5 text-right">Monto original</th>
                        <th className="px-2 py-1.5 text-right">Saldo insoluto</th>
                        <th className="px-2 py-1.5 text-right">Días atraso</th>
                        <th className="px-2 py-1.5">Estatus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {disposiciones.map((d) => (
                        <tr key={d.id} className="border-t border-slate-100" data-testid={`${testidBase}-disp-${d.id}`}>
                          <td className="px-2 py-1.5 font-mono">{d.numero_credito}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">{mxn(d.monto_original)}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">{mxn(d.saldo_insoluto)}</td>
                          <td className="px-2 py-1.5 text-right">{d.dias_atraso || 0}</td>
                          <td className="px-2 py-1.5"><StatusBadge value={d.estatus} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
